import { collection, doc, getDoc, getDocs, query, where, setDoc, orderBy, updateDoc, serverTimestamp, addDoc, arrayUnion, Timestamp, onSnapshot, arrayRemove, limit } from 'firebase/firestore';
import { firebase } from '../firebase-config';
import { BaseUrl, COLLECTIONS } from '../config';
import { getAuth } from 'firebase/auth';
import {
  deleteItem,
  updateItem,
  addItem,
} from './firebaseBootstrap';
import dayjs from "dayjs";
import moment from "moment";
import axiosInstance from '../axios';
import { getLoginToken } from '../storage';
import {
  enrichSupplyItemsWithCatalogSnapshots,
} from './supplyOrderItemDisplay';
const auth = getAuth();

const collectionSupplies = collection(firebase, COLLECTIONS.SUPPLIES_CLEANINGS);
const collectionOrder = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);

async function loadCompanyCategoriesMap(companyId) {
  if (!companyId) return {};
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);
  const q = query(colRef, where("companyId", "==", companyId));
  const snap = await getDocs(q);
  const byId = {};
  snap.docs.forEach((d) => {
    byId[d.id] = { id: d.id, ...d.data() };
  });
  return byId;
}

async function applySupplyItemSnapshots(supplyItems, companyId) {
  if (!Array.isArray(supplyItems) || !companyId) return supplyItems;
  const categoriesById = await loadCompanyCategoriesMap(companyId);
  return enrichSupplyItemsWithCatalogSnapshots(supplyItems, categoriesById);
}

export async function getSuppliesListCount(formData, onSuccess, onError) {
  const colRef = collectionOrder;
  const statuses = formData?.status || [];
  const companyId = formData?.companyId || null;

  if (statuses.length === 0) {
    onSuccess?.([{}]);
    return;
  }

  const chunkSize = 10;
  const chunks = [];

  for (let i = 0; i < statuses.length; i += chunkSize) {
    chunks.push(statuses.slice(i, i + chunkSize));
  }

  try {
    const queries = chunks.map((chunk) => {
      const filters = [where("status", "in", chunk)];
      if (companyId) filters.push(where("companyId", "==", companyId));
      return getDocs(query(colRef, ...filters));
    });

    const snapshots = await Promise.all(queries);

    const statusCount = {};

    snapshots.flatMap(snapshot => snapshot.docs).forEach((doc) => {
      const status = doc.data()?.status;
      if (status) {
        statusCount[status] = (statusCount[status] || 0) + 1;
      }
    });

    statuses.forEach((status) => {
      if (!statusCount[status]) {
        statusCount[status] = 0;
      }
    });

    onSuccess?.([statusCount]);
  } catch (error) {
    console.error("Error getting supplies count:", error);
    onError?.(error);
  }
}

export async function getSupplyOrderList(formData, onSuccess, onError, limitCount = 500) {
  const { companyId, status, statusNotIn } = formData || {};
  const colRef = collectionOrder;

  try {
    const conditions = [];

    if (companyId) conditions.push(where("companyId", "==", companyId));
    if (status) conditions.push(where("status", "==", status));
    if (Array.isArray(statusNotIn) && statusNotIn.length > 0 && statusNotIn.length <= 10) {
      conditions.push(where("status", "not-in", statusNotIn));
    }
    
    // Add limit to prevent excessive data loading
    conditions.push(limit(limitCount));

    const q = conditions.length > 0 ? query(colRef, ...conditions) : colRef;

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const results = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            ...(data.content || {}),
          };
        });
        onSuccess && onSuccess(results);
      },
      (error) => {
        console.error("subscribeToSupplyOrderList error:", error);
        onError && onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("subscribeToSupplyOrderList setup error:", error);
    onError && onError(error);
    return () => {};
  }
}

export async function getSuppliesByDate(formData, onSuccess, onError) {
  const colRef = collectionOrder;
  try {
    const startTimestamp = dayjs(formData.startDate).valueOf();
    const endTimestamp = dayjs(formData.endDate).valueOf(); 

    const filteredQuery = query(
      colRef,
      where("dateOfRequest", ">=", startTimestamp),
      where("dateOfRequest", "<=", endTimestamp)
    );

    const querySnapshot = await getDocs(filteredQuery);
    let results = [];
    querySnapshot.forEach((doc) => {
      let data = doc.data();
      results.push({ id: doc.id, ...data, ...(data?.content || {}) });
    });
    
    onSuccess && onSuccess(results);
    return results;
  } catch (error) {
    console.log("Error getting documents: ", error);
    onError && onError(error);
  }
}

const getSupplyItemCategory = (item) => {
  return (
    item?.categoryName ||
    item?.category ||
    item?.parentCategoryName ||
    item?.department ||
    "Uncategorized"
  );
};

const getNormalizedOrder = (order = {}) => {
  return { ...order, ...(order?.content || {}) };
};

const getOrderClientId = (order = {}) => {
  const normalizedOrder = getNormalizedOrder(order);
  return normalizedOrder?.clientId || normalizedOrder?.client?.id || "";
};

const getOrderCompanyId = (order = {}) => {
  const normalizedOrder = getNormalizedOrder(order);
  return normalizedOrder?.companyId || normalizedOrder?.company?.id || "";
};

const getOrderSupplyItems = (order = {}) => {
  const normalizedOrder = getNormalizedOrder(order);
  if (Array.isArray(normalizedOrder?.supplyItems)) return normalizedOrder?.supplyItems;
  return [];
};

const normalizeSupplyItemStatus = (itemStatus, orderStatus) => {
  if (itemStatus === true) return "Ordered";
  return itemStatus || orderStatus || "Pending";
};

const getSupplyItemId = (item) => {
  return item?.id || item?.supplyId || item?.title || item?.name || "Unknown Supply";
};

const getSupplyItemName = (item) => {
  return item?.title || item?.name || "Unknown Supply";
};

export function aggregateSupplyUsageReport(records = [], filters = {}) {
  const { clientId, category = "All" } = filters;
  const groupedRecords = {};
  const requestIds = new Set();
  let totalQuantityRequested = 0;
  let totalDelivered = 0;
  let pendingRequestCount = 0;

  records.forEach((order) => {
    const normalizedOrder = getNormalizedOrder(order);
    const orderClientId = getOrderClientId(normalizedOrder);
    const supplyItems = getOrderSupplyItems(normalizedOrder);
    if (!normalizedOrder) return;
    if (clientId && String(orderClientId) !== String(clientId)) return;
    if (!supplyItems?.length) return;

    let hasPendingInOrder = false;
    let hasMatchingItem = false;

    supplyItems?.forEach((item) => {
      const itemCategory = getSupplyItemCategory(item);
      if (category !== "All" && category && itemCategory !== category) return;

      hasMatchingItem = true;

      const normalizedStatus = normalizeSupplyItemStatus(item?.status, normalizedOrder?.status);
      const qty = Number(item?.qty) || 0;
      const isDelivered = normalizedStatus === "Delivered";
      const isApproved = ["Approved", "Ordered", "Delivered"].includes(normalizedStatus);
      const isRejected = normalizedStatus === "Rejected";
      const pendingQty = !isDelivered && !isRejected ? qty : 0;
      const groupKey = `${orderClientId || "unknown-client"}__${getSupplyItemId(item)}`;

      if (!groupedRecords[groupKey]) {
        groupedRecords[groupKey] = {
          clientId: orderClientId || "",
          supplyId: getSupplyItemId(item),
          supplyName: getSupplyItemName(item),
          category: itemCategory,
          totalRequestedQty: 0,
          totalApprovedQty: 0,
          totalDeliveredQty: 0,
          pendingQty: 0,
          lastRequestedTs: 0,
          requestedByIds: new Set(),
          requestIds: new Set(),
        };
      }

      groupedRecords[groupKey].totalRequestedQty += qty;
      groupedRecords[groupKey].totalApprovedQty += isApproved ? qty : 0;
      groupedRecords[groupKey].totalDeliveredQty += isDelivered ? qty : 0;
      groupedRecords[groupKey].pendingQty += pendingQty;
      groupedRecords[groupKey].lastRequestedTs = Math.max(
        groupedRecords[groupKey].lastRequestedTs,
        Number(normalizedOrder?.dateOfRequest) || 0
      );
      groupedRecords[groupKey].requestIds.add(normalizedOrder?.id);
      if (normalizedOrder?.createdBy) groupedRecords[groupKey].requestedByIds.add(normalizedOrder?.createdBy);

      totalQuantityRequested += qty;
      totalDelivered += isDelivered ? qty : 0;
      if (pendingQty > 0) hasPendingInOrder = true;
    });

    if (hasMatchingItem) requestIds.add(normalizedOrder?.id);
    if (hasPendingInOrder) pendingRequestCount += 1;
  });

  const rows = Object.values(groupedRecords)
    .map((item) => ({
      ...item,
      requestedByIds: Array.from(item?.requestedByIds || []),
      requestIds: Array.from(item?.requestIds || []),
    }))
    .sort((a, b) => {
      if (a.clientId < b.clientId) return -1;
      if (a.clientId > b.clientId) return 1;
      if (a.supplyName < b.supplyName) return -1;
      if (a.supplyName > b.supplyName) return 1;
      return 0;
    });

  return {
    rows,
    summary: {
      totalRequests: requestIds.size,
      totalQuantityRequested,
      totalDelivered,
      pendingRequests: pendingRequestCount,
    },
  };
}

export async function getSupplyUsageReport(formData, companyId, onSuccess, onError) {
  try {
    const supplyOrders = await getSuppliesByDate(formData);
    const companyFilteredOrders = (supplyOrders || []).filter(
      (item) => String(getOrderCompanyId(item)) === String(companyId)
    );
    const aggregated = aggregateSupplyUsageReport(companyFilteredOrders, {
      clientId: formData?.clientId,
      category: formData?.category,
    });
    onSuccess && onSuccess(aggregated);
    return aggregated;
  } catch (error) {
    onError && onError(error);
    throw error;
  }
}

const normalizeUrgentPriorityValue = (priority) => {
  const value = String(priority || "").trim().toLowerCase();
  if (!value) return "";
  if (["all", "*"].includes(value)) return "All";
  if (["1", "urgent", "high"].includes(value)) return "High";
  if (["2", "medium"].includes(value)) return "Medium";
  if (["3", "low"].includes(value)) return "Low";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const toTimestampMs = (rawValue) => {
  if (!rawValue && rawValue !== 0) return null;
  if (typeof rawValue === "number") return rawValue;
  if (rawValue?.toMillis && typeof rawValue.toMillis === "function") return rawValue.toMillis();
  if (rawValue?.seconds) {
    const nanos = Number(rawValue?.nanoseconds || 0);
    return (rawValue.seconds * 1000) + Math.floor(nanos / 1000000);
  }
  const parsed = dayjs(rawValue).valueOf();
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveOrderRequestedAt = (order = {}) => {
  return toTimestampMs(order?.dateOfRequest) || toTimestampMs(order?.createdAt);
};

const resolveOrderFulfilledAt = (order = {}) => {
  // Urgent supply orders do not consistently store delivered/fulfilled timestamps.
  // Use updatedAt as the practical fulfillment surrogate for reporting.
  return toTimestampMs(order?.updatedAt);
};

export function aggregateUrgentRequestsReport(records = [], filters = {}) {
  const {
    clientId,
    priority = "All",
    slaHours = null,
  } = filters;

  const selectedPriority = normalizeUrgentPriorityValue(priority || "All");
  const rows = [];
  let totalTurnaroundMs = 0;
  let fulfilledCount = 0;
  let pendingCount = 0;
  let delayedCount = 0;

  (records || []).forEach((order) => {
    const normalizedOrder = getNormalizedOrder(order);
    const orderClientId = getOrderClientId(normalizedOrder);
    if (clientId && String(orderClientId) !== String(clientId)) return;

    const orderPriority = normalizeUrgentPriorityValue(normalizedOrder?.priority);
    if (selectedPriority && selectedPriority !== "All" && orderPriority !== selectedPriority) return;

    const supplyItems = getOrderSupplyItems(normalizedOrder);
    if (!supplyItems?.length) return;

    const requestedAtTs = resolveOrderRequestedAt(normalizedOrder);
    const fulfilledAtTs = resolveOrderFulfilledAt(normalizedOrder);
    const turnaroundMs =
      requestedAtTs !== null && fulfilledAtTs !== null
        ? Math.max(fulfilledAtTs - requestedAtTs, 0)
        : null;
    const turnaroundHours = turnaroundMs !== null ? (turnaroundMs / (1000 * 60 * 60)) : null;
    const normalizedStatus = String(normalizedOrder?.status || "Pending");
    const isPending = ["pending", "ordered", "approved"].includes(normalizedStatus.toLowerCase());
    const sla = Number(slaHours);
    const nowMs = Date.now();
    const pendingAgeHours =
      isPending && requestedAtTs !== null ? ((nowMs - requestedAtTs) / (1000 * 60 * 60)) : null;
    const isDelayed = Boolean(
      sla &&
      ((turnaroundHours !== null && turnaroundHours > sla) ||
        (pendingAgeHours !== null && pendingAgeHours > sla))
    );

    const requestedBy = normalizedOrder?.createdBy || normalizedOrder?.updatedBy || "";
    const totalQty = supplyItems.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0);
    const itemDetails = supplyItems.map((item) => ({
      name: getSupplyItemName(item),
      qty: Number(item?.qty) || 0,
    }));

    rows.push({
      id: normalizedOrder?.id,
      requestId: normalizedOrder?.id,
      clientId: orderClientId,
      supplyName: supplyItems.map((item) => getSupplyItemName(item)).join(", "),
      supplyItems: `${supplyItems.length} items`,
      itemDetails,
      quantity: totalQty,
      priority: orderPriority || "High",
      status: normalizedStatus,
      requestedAtTs: requestedAtTs || 0,
      fulfilledAtTs: fulfilledAtTs || 0,
      requestedAt: requestedAtTs || null,
      fulfilledAt: fulfilledAtTs || null,
      turnaroundMs,
      turnaroundHours,
      requestedBy,
      isDelayed,
    });

    if (isPending) pendingCount += 1;
    if (turnaroundMs !== null) {
      totalTurnaroundMs += turnaroundMs;
      fulfilledCount += 1;
    }
    if (isDelayed) delayedCount += 1;
  });

  rows.sort((a, b) => b.requestedAtTs - a.requestedAtTs);

  return {
    rows,
    summary: {
      totalUrgentRequests: rows.length,
      pendingUrgentRequests: pendingCount,
      avgFulfillmentMs: fulfilledCount ? Math.round(totalTurnaroundMs / fulfilledCount) : 0,
      delayedRequests: delayedCount,
    },
  };
}

export async function getUrgentRequestsReport(formData, companyId, onSuccess, onError) {
  try {
    const baseOrders = await new Promise((resolve, reject) => {
      getSuppliesReports(
        formData,
        "All",
        companyId,
        (result) => resolve(result || []),
        (err) => reject(err)
      );
    });

    const normalizedOrders = (baseOrders || []).map(getNormalizedOrder);
    const aggregated = aggregateUrgentRequestsReport(normalizedOrders, {
      clientId: formData?.clientId,
      priority: formData?.priority || "All",
      slaHours: formData?.slaHours,
    });

    onSuccess && onSuccess(aggregated);
    return aggregated;
  } catch (error) {
    onError && onError(error);
    throw error;
  }
}

export async function fetchLatestSupplies(formData, onSuccess, onError) {
  const colRef = collectionOrder;
  let latestDate = null;

  try {
    const companyId = formData.companyId;

    const q = query(
      colRef,
      where("companyId", "==", companyId),
      orderBy("dateOfRequest", "desc"),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const latestDoc = snapshot.docs[0].data();
      latestDate = latestDoc;
    }

    if (onSuccess) onSuccess(latestDate);

    return latestDate;
  } catch (error) {
    console.error("Error getting latest supply:", error);
    if (onError) onError(error);
    return null;
  }
}


export async function getSuppliesReports(formData, empName, companyId, onSuccess, onError) {
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);
  const startTimestamp = dayjs(formData.startDate).valueOf();
  const endTimestamp = dayjs(formData.endDate).valueOf(); 

  const baseConstraints = [
    where("dateOfRequest", ">=", startTimestamp),
    where("dateOfRequest", "<=", endTimestamp),
    where("companyId", "==", companyId)
  ];

  const results = [];
  const isAll = !empName || empName === "All";
  try {
    if (isAll) {
      const q = query(colRef, ...baseConstraints);
      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
    }
    else if (typeof empName === "string") {
      const q = query(colRef, ...baseConstraints, where("createdBy", "==", empName));
      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
    }
    onSuccess && onSuccess(results);
  } catch (error) {
    console.error("Error getting documents:", error);
    onError && onError(error);
  }
}

export function getSuppliesNotesReports(formData, companyId, onSuccess, onError) {
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER_NOTES);
  const startMillis = dayjs(formData.startDate).startOf("day").valueOf();
  const endMillis = dayjs(formData.endDate).endOf("day").valueOf();
  const startTimestamp = Timestamp.fromMillis(startMillis);
  const endTimestamp = Timestamp.fromMillis(endMillis);

  const baseConstraints = [
    where("createdAt", ">=", startTimestamp),
    where("createdAt", "<=", endTimestamp),
    where("companyId", "==", companyId),
  ];

  const executor = async (resolve, reject) => {
    const results = [];
    try {
      const q = query(colRef, ...baseConstraints);
      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      onSuccess && onSuccess(results);
      resolve(results);
    } catch (error) {
      console.error("Error getting documents:", error);
      onError && onError(error);
      reject(error);
    }
  };

  return new Promise(executor);
}


export async function getSupplyOrderListBasedOnId(id, onSuccess, onError) {
  const docRef = doc(collectionOrder, id);

  try {
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const result = { id: docSnapshot.id, ...data, ...data.content };
      onSuccess && onSuccess(result);
    } else {
      // Document does not exist
      onSuccess && onSuccess(null);
    }
  } catch (error) {
    console.error('Error getting document: ', error);
    onError && onError(error);
  }
}

export async function getSuppliesList(formData, onSuccess, onError) {
  const colRef = collectionSupplies;

  try {
    let results = [];

    if (formData.status && formData.status.length > 0) {
      const q = query(colRef, where('status', 'in', [...formData.status]), limit(500));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        results.push({ id: doc.id, ...data });
      });
    } else {
      const q = query(colRef, limit(500));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        results.push({ id: doc.id, ...data, ...data.content });
      });
    }

    onSuccess && onSuccess(results);
  } catch (error) {
    console.error('Error getting documents: ', error);
    onError && onError(error);
  }
}

export async function getSupplies(id, onSuccess, onError) {
  const supplyItemColRef = collectionSupplies;
  const supplyDocRef = doc(supplyItemColRef, id);

  try {
    const docSnapshot = await getDoc(supplyDocRef);

    if (!docSnapshot.exists()) {
      onError && onError({ status: false, error: 'Document not found' });
      return;
    }

    const data = docSnapshot.data();
    const docContent = { id: docSnapshot.id, ...data };

    // Fetching the associated SupplyItems
    const supplyItemsSnapshot = await getDocs(collection(supplyItemColRef, id, 'SupplieItems'));
    const suppliesItems = [];

    supplyItemsSnapshot.forEach((document) => {
      const docData = document.data();
      suppliesItems.push({ id: document.id, clientName: docContent.client?.name, ...docData });
    });

    onSuccess({
      status: true,
      data: docContent,
      SupplieItems: suppliesItems,
    });
  } catch (error) {
    console.error('Error getting document: ', error);
    onError && onError({ status: false, error: error });
  }
}

export async function getSuppliesByClientItems(formData, onSuccess, onError) {
  if (formData?.Clients && formData.Clients.length > 0) {
    const supplyItemColRef = collectionSupplies;
    const q = query(supplyItemColRef, where('client.name', 'in', [...formData.Clients]), orderBy('dateOfRequest', 'desc'));

    try {
      const querySnapshot = await getDocs(q);
      const results = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        results.push({ id: doc.id, ...data });
      });

      onSuccess && onSuccess(results);
    } catch (error) {
      console.error('Error getting documents: ', error);
      onError && onError(error);
    }
  }
}

export async function addSupplies(formData, onSuccess, onError) {
  addItem('users', formData, onSuccess, onError);
}

export async function updateSupplies(id, formData, onSuccess, onError) {
  updateItem(collectionSupplies, id, formData, onSuccess, onError);
}

export async function updateSuppliesItemsData(
  id,
  SupplieItemsId,
  formData,
  saveStatus,
  onSuccess,
  onError
) {

  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);
  const docRef = doc(colRef, id);

  try {
    if (saveStatus === 'Approved') {
      await updateDoc(docRef, { status: saveStatus, orderStatus: 'Pending' });

      const itemRef = doc(docRef, 'SupplieItems', SupplieItemsId);
      const docSnap = await getDoc(itemRef);

      if (!docSnap.exists()) {
        onError({ status: false, error: 'Document not found' });
        return;
      }

      const oldDocData = docSnap.data();
      await updateDoc(itemRef, {
        ...oldDocData,
        ...formData,
      });

      onSuccess && onSuccess({ status: true, data: { ...oldDocData, ...formData } });
      console.log('updated');
    } else {
      const itemRef = doc(docRef, 'SupplieItems', SupplieItemsId);
      const docSnap = await getDoc(itemRef);

      if (!docSnap.exists()) {
        onError({ status: false, error: 'Document not found' });
        return;
      }

      const oldDocData = docSnap.data();
      await updateDoc(itemRef, {
        ...oldDocData,
        ...formData,
      });

      onSuccess && onSuccess({ status: true, data: { ...oldDocData, ...formData } });
    }
  } catch (error) {
    onError && onError(error);
  }
}


export async function saveRejectionReson(id, SupplieItemsId, formData, onSuccess, onError) {
  try {
    const itemRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_ORDER), id, 'SupplieItems', SupplieItemsId);
    await updateDoc(itemRef, { ...formData });
    onSuccess && onSuccess({ status: true });
  } catch (error) {
    onError && onError(error);
  }
}

export async function deleteSupplies(id, onSuccess, onError) {
  if (!id || id === '') {
    const docRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_ORDER), id);
    await deleteItem(docRef);
    onSuccess && onSuccess({ status: true });
  } else {
    onError && onError({ status: false, message: 'Invalid input' });
  }
}

export async function updateSuppliesStatus(id, formData, name, onSuccess, onError) {
  const currentUserId = auth.currentUser.uid;

  if (!id || id.trim() === "") {
    onError && onError({ status: false, message: "Id is Missing" });
    return;
  }

  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);
    const updateData = {
      ...formData,
      finalizedTime: serverTimestamp(),
      finalizedBy: {
        id: currentUserId,
        name: name
      },
      updatedBy: currentUserId,
      updatedAt: serverTimestamp(),
      ...(formData.status === "Rejected" && { archive: true }),
      ...(formData.status === "Approved" && { approvedTime: serverTimestamp() }),
    };

    const docRef = doc(colRef, id);
    await updateDoc(docRef, updateData);
    onSuccess && onSuccess({ status: true, data: formData });

  } catch (error) {
    console.error("Error updating document:", error);
    onError && onError({ status: false, message: error.message });
  }
}

export async function getCategories(companyId, onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);
    const q = companyId ? query(colRef, where("companyId", "==", companyId)) : colRef;

    const querySnapshot = await getDocs(q);
    const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    onSuccess && onSuccess(results);
  } catch (error) {
    console.log("Error getting documents: ", error);
    onError && onError(error);
  }
}

/**
 * Add supply order note via cloud function (translation + attachments).
 * @param {Object} formData - { orderId, companyId, note, createBy, clientId?, messageLanguage? }
 * @param {File[]} [files]
 */
export async function addSupplyOrderNote(formData, files = [], onSuccess, onError) {
  try {
    const payload = new FormData();
    payload.append("orderId", formData.orderId);
    payload.append(
      "data",
      JSON.stringify({
        companyId: formData.companyId,
        note: formData.note,
        createBy: formData.createBy,
        clientId: formData.clientId,
        messageLanguage: formData.messageLanguage,
      })
    );

    if (Array.isArray(files) && files.length > 0) {
      files.forEach((file, index) => {
        payload.append(`file${index}`, file);
      });
    }

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const response = await axiosInstance.post(`${BaseUrl}addSupplyOrderNote`, payload, {
      headers: {
        ...authHeaders,
      },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add supply order note";
    if (onError) {
      onError({ status: false, error: message });
    }
    throw error;
  }
}

/** @deprecated Use addSupplyOrderNote — kept for existing call sites. */
export async function addNotes(formData, filesOrSuccess, onSuccessOrError, onError) {
  const hasFilesArg = filesOrSuccess !== undefined && typeof filesOrSuccess !== "function";
  const files = hasFilesArg ? filesOrSuccess || [] : [];
  const onSuccess = hasFilesArg ? onSuccessOrError : filesOrSuccess;
  const onErrorCb = hasFilesArg ? onError : onSuccessOrError;
  return addSupplyOrderNote(formData, files, onSuccess, onErrorCb);
}

export async function fetchNotes(id, onSuccess, onError) {
  if (!id) {
    if (onError) {
      onError(new Error('No ID provided'));
    }
    return;
  }

  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER_NOTES);
  const q = query(colRef, where('orderId', '==', id));

  try {
    const querySnapshot = await getDocs(q);
    const notes = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    if (notes.length === 0) {
      notes.push({ message: 'No Available Notes' });
    }
    onSuccess && onSuccess(notes);
  } catch (error) {
    onError && onError(error);
  }
}

export function subscribeNotes({ companyId}, onSuccess, onError) {
  if (!companyId) {
    if (onError) onError(new Error("No companyId provided"));
    return () => {};
  }

  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER_NOTES);

  const conditions = [where("companyId", "==", companyId)];

  const q = query(colRef, ...conditions);

  try {
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const notes = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (notes.length === 0) {
          onSuccess && onSuccess([{ message: "No Available Notes" }]);
        } else {
          onSuccess && onSuccess(notes);
        }
      },
      (error) => {
        console.error("subscribeNotes error:", error);
        onError && onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("subscribeNotes setup error:", error);
    onError && onError(error);
    return () => {};
  }
}

export async function closeSupplyOrder(id, onSuccess, onError) {
  if (!id) return onError && onError("Invalid order ID");

  const currentUserId = auth.currentUser?.uid;
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);

  try {
    const docRef = doc(colRef, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return onError && onError("No such document!");
    }

    const data = docSnap.data();

    let updatedSupplyItems = data?.supplyItems?.map(item => ({
      ...item,
      status: "Delivered"
    }));
    updatedSupplyItems = await applySupplyItemSnapshots(
      updatedSupplyItems,
      data?.companyId
    );

    const now = serverTimestamp();

    await updateDoc(docRef, {
      supplyItems: updatedSupplyItems,
      updatedBy: currentUserId,
      status: "Delivered",
      updatedAt: now,
      archive: true,
      archiveAt: now
    });

    onSuccess && onSuccess({
      id: docSnap.id,
      ...data,
      supplyItems: updatedSupplyItems,
      updatedBy: currentUserId,
      status: "Delivered",
      updatedAt: now,
      archive: true,
      archiveAt: now
    });

  } catch (error) {
    onError && onError(error);
  }
};


export async function updateSupplyItemsStatus(id, newStatus, itemId, onSuccess, onError, fullUpdatedArray = null) {
  const currentUserId = auth.currentUser.uid;
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);

  try {
    const docRef = doc(colRef, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      let updatedSupplyItems;

      if (fullUpdatedArray) {
        updatedSupplyItems = fullUpdatedArray;
      } else {
      if (Array.isArray(itemId)) {
        updatedSupplyItems = data.supplyItems.map(item => {
          if (itemId.includes(item.id)) {
            return {
              ...item,
              status: newStatus
            };
          }
          return item;
        });
      } else if (itemId) {
        updatedSupplyItems = data.supplyItems.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              status: newStatus
            };
          }
          return item;
        });
      } else {
        updatedSupplyItems = data.supplyItems.map(item => ({
          ...item,
          status: newStatus
        }));
      }
    }

      updatedSupplyItems = await applySupplyItemSnapshots(
        updatedSupplyItems,
        data?.companyId
      );

      const now = serverTimestamp();

      await updateDoc(docRef, {
        supplyItems: updatedSupplyItems,
        updatedBy: currentUserId,
        updatedAt: now
      });

      onSuccess && onSuccess({ id: docSnap.id, ...data, supplyItems: updatedSupplyItems, updatedAt: now });
    } else {
      onError && onError("No such document!");
    }
  } catch (error) {
    onError && onError(error);
  }
}

export async function updateSupplyItemDate(id, itemId, date, onSuccess, onError) {
  const currentUserId = auth.currentUser.uid;
  const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);

  try {
    const docRef = doc(colRef, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const updatedSupplyItems = data.supplyItems.map(item => {
        if (item.id === itemId) {
          const timestamp = Timestamp.fromDate(new Date(date));
          return { ...item, deliveryDate: timestamp };
        }
        return item;
      });

      const now = serverTimestamp();

      await updateDoc(docRef, {
        supplyItems: updatedSupplyItems,
        updatedBy: currentUserId,
        updatedAt: now
      });

      onSuccess && onSuccess({ id: docSnap.id, ...data, supplyItems: updatedSupplyItems, updatedAt: now });
    } else {
      onError && onError("No such document!");
    }
  } catch (error) {
    console.error("Error updating supply item date:", error);
    onError && onError(error);
  }
}


export async function supplyRejectReasons(id, result, onSuccess, onError) {
  const currentUserId = auth.currentUser.uid;

  if (!id || id.trim() === "") {
    onError && onError({ status: false, message: "Id is Missing" });
    return;
  }

  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);
    const docRef = doc(colRef, id);

    const newReason = {
      reason: result.reason,
      message: result.message,
    };

    await updateDoc(docRef, {
      rejectReason: arrayUnion(newReason),
      updatedBy: currentUserId,
      updatedAt: serverTimestamp(),
    });

    onSuccess && onSuccess({ status: true });
  } catch (error) {
    console.error("Error updating document:", error);
    onError && onError({ status: false, message: error.message });
  }
}

export async function updateArchive(id, formData, onSuccess, onError) {
  const currentUserId = auth.currentUser?.uid;

  if (!id || id.trim() === "") {
    onError && onError({ status: false, message: "Id is Missing" });
    return;
  }

  try {
    const updateData = {
      ...formData,
      updatedBy: currentUserId,
      updatedAt: serverTimestamp(),
    };

    const docRef = doc(firebase, COLLECTIONS.SUPPLIES_ORDER, id);
    await setDoc(docRef, updateData, { merge: true });

    onSuccess && onSuccess({ status: true, data: formData });
  } catch (error) {
    console.error("Error updating document:", error);
    onError && onError({ status: false, message: error.message });
  }
}

export async function UpdateMsgFalse(formData, onSuccess, onError) {
  const currentUser = auth.currentUser;
  const currentUserId = currentUser?.uid;

  if (!currentUserId) {
    if (onError) onError({ status: false, error: "User not authenticated" });
    return;
  }

  try {
    const suppliesDocRef = doc(firebase, COLLECTIONS.SUPPLIES_ORDER, formData.id);

    await updateDoc(suppliesDocRef, {
      readUser: arrayUnion(currentUserId),
      updatedAt: serverTimestamp(),
    });

    if (onSuccess) {
      onSuccess({
        status: true,
        data: {
          id: formData.id,
          updatedAt: new Date(),
        },
      });
    }
  } catch (error) {
    if (onError) {
      onError({ status: false, error: error.message || error });
    }
  }
}

export const getIndustriesList = async (formData, onSuccess, onError) => {
  
  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_INDUSTRY);

    const querySnapshot = await getDocs(colRef);

    const results = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    if (onSuccess) {
      onSuccess(results);
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    if (onError) {
      onError(error);
    }
  }
};

export const getIndustryServices = async (formData, onSuccess, onError) => {
  
  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_CATEGORY);

    const querySnapshot = await getDocs(colRef);

    const results = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    if (onSuccess) {
      onSuccess(results);
    }
    return results;
  } catch (error) {
    console.error('Error getting documents:', error);
    if (onError) {
      onError(error);
    }
  }
};

export async function getDeliveredAndArchivedSupplies(onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER);
    const startMs = moment().subtract(90, "days").startOf("day").valueOf();
    const endMs = moment().endOf("day").valueOf();

    const q = query(colRef, where("status", "in", ["Delivered", "Pending"]));

    const querySnapshot = await getDocs(q);

    const supplies = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const createdRaw = data?.createdAt;
      const createdMs =
        typeof createdRaw === "number"
          ? createdRaw
          : createdRaw?.toMillis?.() ||
            (createdRaw?.seconds ? createdRaw.seconds * 1000 : new Date(createdRaw).getTime());
      if (Number.isNaN(createdMs)) return;
      if (createdMs >= startMs && createdMs <= endMs) {
        supplies.push({ id: doc.id, ...data });
      }
    });

    onSuccess && onSuccess({ status: true, data: supplies });
  } catch (error) {
    onError && onError({ status: false, error });
  }
}


/**
 * Fetch all predefined supply categories and items organized by parent-child relationships
 * Only returns items that have valid parent references (no orphaned items)
 * @param {Function} onSuccess - Callback with array of properly organized predefined supplies
 * @param {Function} onError - Callback with error
 */
export async function getPredefinedSupplies(onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_CATEGORY);
    const querySnapshot = await getDocs(colRef);
    const allSupplies = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Filter: Get only parent categories (no parentCategoryId)
    const parentCategories = allSupplies.filter(item => !item.parentCategoryId && !item.deleted);
    
    // Create a map of parent IDs for validation
    const parentIdMap = new Set(parentCategories.map(p => p.id));

    // Filter: Get only child categories with valid parent references
    const childCategories = allSupplies.filter(
      item => item.parentCategoryId && parentIdMap.has(item.parentCategoryId) && !item.deleted
    );

    // Combine and sort
    const validSupplies = [...parentCategories, ...childCategories].sort((a, b) => {
      if (a.orders && b.orders) return a.orders - b.orders;
      return 0;
    });


    onSuccess && onSuccess({ status: true, data: validSupplies });
    return validSupplies;
  } catch (error) {
    console.error("Error fetching predefined supplies:", error);
    onError && onError({ status: false, error });
    throw error;
  }
}

/**
 * Add selected predefined items to company supplies
 * @param {Object} formData - Contains companyId and selectedItems array
 * @param {Function} onSuccess - Callback on success
 * @param {Function} onError - Callback on error
 */
export async function addPredefinedSupplyItems(formData, onSuccess, onError) {
  try {
    const response = await axiosInstance.post(
      `${BaseUrl}addPredefinedSupplyItems`,
      formData
    );

    onSuccess && onSuccess(response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding predefined supply items:", error);
    onError && onError(error);
    throw error;
  }
}

// export async function saveSuppliesCategory() {
//   try {
//     const companyId = "ckMwUiLZrTiVpJ6RIELG"; 
//     const supplyCategoriesRef = collection(firebase, COLLECTIONS.SUPPLIES_CATEGORY);
//     const supplyCategoriesSnapshotAll = await getDocs(supplyCategoriesRef);
//     const supplyCategories = supplyCategoriesSnapshotAll.docs.map(doc => ({ id: doc.id, ...doc.data() }));

//     let categoryIdMap = {}; 
//     let newCategories = [];

//     const topLevelCategories = supplyCategories.filter(category => !category.parentCategoryId);

//     topLevelCategories.forEach(category => {
//       const newId = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY)).id;
//       categoryIdMap[category.id] = newId;

//       newCategories.push({
//         ...category,
//         id: newId,
//         companyId,
//         createdAt: serverTimestamp(),
//       });
//     });

//     const childCategories = supplyCategories.filter(category => category.parentCategoryId && categoryIdMap[category.parentCategoryId]);

//     childCategories.forEach(category => {
//       const newId = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY)).id;
//       categoryIdMap[category.id] = newId;

//       newCategories.push({
//         ...category,
//         id: newId,
//         parentCategoryId: categoryIdMap[category.parentCategoryId],
//         companyId,
//         createdAt: serverTimestamp(),
//       });
//     });

//     const batch = writeBatch(firebase);
//     newCategories.forEach(category => {
//       const categoryRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY), category.id);
//       batch.set(categoryRef, category);
//     });

//     await batch.commit();
//     console.log("Supply categories successfully migrated for company", topLevelCategories, childCategories);
//   } catch (error) {
//     console.error("Migration failed:", error);
//   }
// }